import * as THREE from "three";

// Procedural low-poly models built from shared primitives: no downloads, no external assets.
// Unit models face +x (engine facing 0 = East) and are authored at ~1 hex = 1 unit before the world's 1.85 scale.
// Meshes flagged userData.paint take the faction / selection material from the caller.

const G = {
  box: new THREE.BoxGeometry(1, 1, 1),
  cyl: new THREE.CylinderGeometry(1, 1, 1, 10),
  cyl6: new THREE.CylinderGeometry(1, 1, 1, 6),
  sphere: new THREE.SphereGeometry(1, 10, 8),
  dome: new THREE.SphereGeometry(1, 10, 6, 0, Math.PI * 2, 0, Math.PI / 2),
  cone: new THREE.ConeGeometry(1, 1, 6),
  cone4: new THREE.ConeGeometry(1, 1, 4),
  octa: new THREE.OctahedronGeometry(1),
  rock: new THREE.DodecahedronGeometry(1),
  plane: new THREE.PlaneGeometry(1, 1),
};

const FACTION = {
  1: { paint: 0x5ec8f0, dark: 0x16324a, trim: 0xbfefff },
  2: { paint: 0xf05a48, dark: 0x4a1c16, trim: 0xffc2b8 },
};
export const PALETTE = {
  metal: 0x2b2f36,
  track: 0x1b1e23,
  glass: 0x9fe3ff,
  ore: 0xf0c040,
  orange: 0xe8892c,
  stone: 0x8d939c,
  rock: 0x6b7480,
  spike: 0x454b55,
};

const mats = new Map();
export function mat(color, opts = {}) {
  const k = `${color}-${JSON.stringify(opts)}`;
  if (!mats.has(k)) mats.set(k, new THREE.MeshLambertMaterial({ color, ...opts }));
  return mats.get(k);
}
export const factionColors = (owner) => FACTION[owner] ?? FACTION[1];

function part(geo, material, [sx, sy, sz], [x, y, z], rot) {
  const m = new THREE.Mesh(geo, material);
  m.scale.set(sx, sy, sz);
  m.position.set(x, y, z);
  if (rot) m.rotation.set(rot[0] ?? 0, rot[1] ?? 0, rot[2] ?? 0);
  return m;
}
const paintPart = (...a) => { const m = part(...a); m.userData.paint = true; return m; };

// ---------------------------------------------------------------- units
function soldier(paint, dark, x, z) {
  const g = new THREE.Group();
  g.position.set(x, 0, z);
  g.add(
    part(G.box, dark, [0.07, 0.14, 0.09], [0, 0.07, 0]),                              // legs
    paintPart(G.box, paint, [0.09, 0.14, 0.12], [0, 0.21, 0]),                        // torso
    part(G.box, dark, [0.05, 0.1, 0.09], [-0.065, 0.22, 0]),                          // pack
    part(G.sphere, mat(0xe0c3a0), [0.045, 0.045, 0.045], [0, 0.32, 0]),               // head
    paintPart(G.dome, paint, [0.056, 0.05, 0.056], [0, 0.33, 0]),                     // helmet
    part(G.box, mat(PALETTE.metal), [0.2, 0.025, 0.025], [0.07, 0.23, 0.05]),         // rifle
  );
  return g;
}

function infantry(paint, dark) {
  const g = new THREE.Group();
  // Fire team of three in a wedge: reads as "infantry" at any zoom.
  g.add(soldier(paint, dark, 0.12, 0), soldier(paint, dark, -0.08, 0.14), soldier(paint, dark, -0.08, -0.14));
  return g;
}

function tank(paint, dark) {
  const g = new THREE.Group();
  const track = mat(PALETTE.track);
  for (const z of [-0.17, 0.17]) {
    g.add(part(G.box, track, [0.62, 0.11, 0.1], [0, 0.065, z]));
    for (const x of [-0.22, -0.07, 0.08, 0.23]) g.add(part(G.cyl, mat(PALETTE.metal), [0.045, 0.11, 0.045], [x, 0.065, z], [Math.PI / 2]));
  }
  g.add(
    paintPart(G.box, paint, [0.56, 0.11, 0.3], [0, 0.16, 0]),                          // hull
    paintPart(G.box, paint, [0.12, 0.07, 0.28], [0.27, 0.13, 0], [0, 0, -0.5]),        // glacis
    paintPart(G.cyl6, paint, [0.14, 0.1, 0.14], [-0.03, 0.27, 0]),                     // turret
    part(G.box, dark, [0.1, 0.04, 0.12], [-0.13, 0.27, 0]),                            // bustle
    part(G.cyl, mat(PALETTE.metal), [0.026, 0.4, 0.026], [0.27, 0.28, 0], [0, 0, Math.PI / 2]),
    part(G.cyl, mat(PALETTE.metal), [0.04, 0.06, 0.04], [0.47, 0.28, 0], [0, 0, Math.PI / 2]), // muzzle
    part(G.cyl, dark, [0.035, 0.04, 0.035], [-0.05, 0.34, 0.06]),                     // hatch
  );
  return g;
}

function artillery(paint, dark) {
  const g = new THREE.Group();
  const metal = mat(PALETTE.metal);
  for (const z of [-0.16, 0.16]) g.add(part(G.cyl, mat(PALETTE.track), [0.11, 0.05, 0.11], [0, 0.11, z], [Math.PI / 2]));
  g.add(
    paintPart(G.box, paint, [0.22, 0.08, 0.26], [0, 0.17, 0]),                         // carriage
    part(G.box, dark, [0.36, 0.035, 0.035], [-0.2, 0.08, 0.08], [0, 0.35, 0.18]),      // split trail
    part(G.box, dark, [0.36, 0.035, 0.035], [-0.2, 0.08, -0.08], [0, -0.35, 0.18]),
    paintPart(G.box, paint, [0.03, 0.16, 0.3], [0.1, 0.26, 0], [0, 0, -0.15]),         // gun shield
    part(G.cyl, metal, [0.05, 0.16, 0.05], [-0.02, 0.24, 0], [0, 0, Math.PI / 2]),     // breech
    part(G.cyl, metal, [0.028, 0.6, 0.028], [0.22, 0.36, 0], [0, 0, Math.PI / 2 - 0.5]), // long barrel, elevated
  );
  return g;
}

function harvester(paint, dark) {
  const g = new THREE.Group();
  const track = mat(PALETTE.track);
  for (const x of [-0.2, 0, 0.2]) for (const z of [-0.17, 0.17]) g.add(part(G.cyl, track, [0.06, 0.06, 0.06], [x, 0.06, z], [Math.PI / 2]));
  g.add(
    paintPart(G.box, paint, [0.58, 0.07, 0.3], [0, 0.12, 0]),                          // chassis
    paintPart(G.box, paint, [0.16, 0.16, 0.26], [0.18, 0.24, 0]),                      // cab
    part(G.box, mat(PALETTE.glass, { emissive: 0x1d4a5c }), [0.02, 0.07, 0.2], [0.265, 0.27, 0]),
    part(G.box, dark, [0.3, 0.13, 0.28], [-0.1, 0.22, 0]),                             // ore bin
    part(G.cyl, mat(PALETTE.metal), [0.07, 0.34, 0.07], [0.34, 0.09, 0], [Math.PI / 2]), // scoop drum
  );
  const ore = mat(PALETTE.ore, { emissive: 0x3a2a00 });
  for (const [x, z, s] of [[-0.14, 0.05, 0.05], [-0.05, -0.06, 0.045], [-0.12, -0.07, 0.04], [-0.04, 0.07, 0.035]]) {
    g.add(part(G.octa, ore, [s, s * 1.3, s], [x, 0.3, z]));
  }
  return g;
}

// Aircraft are modelled at ground level; the world lifts them to flying height and spins parts named "rotor".
function helicopter(paint, dark) {
  const g = new THREE.Group();
  const metal = mat(PALETTE.metal), glass = mat(PALETTE.glass, { emissive: 0x1d4a5c });
  g.add(
    paintPart(G.sphere, paint, [0.22, 0.13, 0.13], [0.04, 0.2, 0]),                      // fuselage
    part(G.sphere, glass, [0.1, 0.08, 0.1], [0.18, 0.23, 0]),                             // canopy
    paintPart(G.cyl, paint, [0.035, 0.36, 0.035], [-0.3, 0.22, 0], [0, 0, Math.PI / 2]),  // tail boom
    paintPart(G.box, paint, [0.08, 0.12, 0.02], [-0.47, 0.27, 0]),                        // tail fin
    part(G.box, dark, [0.18, 0.025, 0.05], [0.02, 0.14, 0.15]),                           // stub wing + pods
    part(G.box, dark, [0.18, 0.025, 0.05], [0.02, 0.14, -0.15]),
    part(G.cyl, metal, [0.03, 0.14, 0.03], [0.05, 0.12, 0.2], [0, 0, Math.PI / 2]),
    part(G.cyl, metal, [0.03, 0.14, 0.03], [0.05, 0.12, -0.2], [0, 0, Math.PI / 2]),
    part(G.box, metal, [0.36, 0.015, 0.015], [0.02, 0.03, 0.09]),                         // skids
    part(G.box, metal, [0.36, 0.015, 0.015], [0.02, 0.03, -0.09]),
    part(G.cyl, metal, [0.02, 0.06, 0.02], [0.04, 0.35, 0]),                              // mast
  );
  const rotor = new THREE.Group();
  rotor.name = "rotor";
  rotor.position.set(0.04, 0.38, 0);
  rotor.add(part(G.box, mat(0x1a1d22), [0.8, 0.008, 0.035], [0, 0, 0]), part(G.box, mat(0x1a1d22), [0.035, 0.008, 0.8], [0, 0, 0]));
  const tail = new THREE.Group();
  tail.name = "rotor";
  tail.userData.axis = "z";
  tail.position.set(-0.47, 0.28, 0.025);
  tail.add(part(G.box, mat(0x1a1d22), [0.16, 0.012, 0.005], [0, 0, 0]));
  g.add(rotor, tail);
  return g;
}

function jet(paint, dark) {
  const g = new THREE.Group();
  const glass = mat(PALETTE.glass, { emissive: 0x1d4a5c });
  const wing = new THREE.Shape();
  wing.moveTo(0.18, 0); wing.lineTo(-0.2, 0.34); wing.lineTo(-0.26, 0.34); wing.lineTo(-0.2, 0); wing.closePath();
  const wingGeo = new THREE.ExtrudeGeometry(wing, { depth: 0.02, bevelEnabled: false });
  wingGeo.rotateX(Math.PI / 2);
  const wl = new THREE.Mesh(wingGeo, paint); wl.userData.paint = true; wl.position.set(0, 0.2, 0);
  const wr = wl.clone(); wr.userData.paint = true; wr.scale.z = -1; wr.position.set(0, 0.18, 0);
  g.add(
    paintPart(G.cyl, paint, [0.06, 0.62, 0.06], [0, 0.2, 0], [0, 0, Math.PI / 2]),         // fuselage
    paintPart(G.cone, paint, [0.06, 0.16, 0.06], [0.39, 0.2, 0], [0, 0, -Math.PI / 2]),    // nose
    part(G.sphere, glass, [0.09, 0.04, 0.04], [0.16, 0.25, 0]),                             // canopy
    paintPart(G.box, paint, [0.1, 0.13, 0.015], [-0.24, 0.29, 0.05], [0.3, 0, 0]),          // twin tails
    paintPart(G.box, paint, [0.1, 0.13, 0.015], [-0.24, 0.29, -0.05], [-0.3, 0, 0]),
    part(G.cyl, dark, [0.045, 0.06, 0.045], [-0.33, 0.2, 0], [0, 0, Math.PI / 2]),          // exhaust
    part(G.sphere, mat(0xff9a3a, { emissive: 0xff6a00 }), [0.03, 0.03, 0.03], [-0.37, 0.2, 0]),
    wl, wr,
  );
  return g;
}

function gunboat(paint, dark) {
  const g = new THREE.Group();
  const metal = mat(PALETTE.metal);
  g.add(
    paintPart(G.box, paint, [0.62, 0.1, 0.22], [0, 0.1, 0]),
    paintPart(G.box, paint, [0.16, 0.08, 0.16], [0.18, 0.18, 0]),
    part(G.box, dark, [0.2, 0.06, 0.18], [-0.12, 0.16, 0]),
    part(G.cyl, metal, [0.02, 0.28, 0.02], [0.28, 0.2, 0], [0, 0, Math.PI / 2]),
    part(G.cyl, dark, [0.03, 0.08, 0.03], [-0.08, 0.22, 0]),
  );
  return g;
}

function destroyer(paint, dark) {
  const g = new THREE.Group();
  const metal = mat(PALETTE.metal);
  g.add(
    paintPart(G.box, paint, [0.86, 0.1, 0.2], [0, 0.1, 0]),
    paintPart(G.box, paint, [0.18, 0.12, 0.16], [0.1, 0.2, 0]),
    part(G.box, dark, [0.22, 0.08, 0.14], [-0.16, 0.18, 0]),
    part(G.cyl, dark, [0.035, 0.16, 0.035], [-0.06, 0.28, 0]),
    paintPart(G.cyl6, paint, [0.08, 0.07, 0.08], [0.22, 0.2, 0]),
    part(G.cyl, metal, [0.018, 0.32, 0.018], [0.38, 0.22, 0], [0, 0, Math.PI / 2]),
    part(G.cyl, metal, [0.018, 0.22, 0.018], [-0.32, 0.2, 0], [0, 0, Math.PI / 2]),
  );
  return g;
}

function battleship(paint, dark) {
  const g = new THREE.Group();
  const metal = mat(PALETTE.metal);
  g.add(
    paintPart(G.box, paint, [1.05, 0.14, 0.28], [0, 0.12, 0]),
    paintPart(G.box, paint, [0.22, 0.12, 0.22], [0, 0.24, 0]),
    part(G.box, dark, [0.3, 0.08, 0.2], [-0.22, 0.22, 0]),
    part(G.cyl, dark, [0.04, 0.2, 0.04], [-0.1, 0.34, 0.04]),
    part(G.cyl, dark, [0.04, 0.16, 0.04], [-0.18, 0.32, -0.04]),
  );
  for (const x of [-0.32, 0.28, 0.42]) {
    g.add(paintPart(G.cyl6, paint, [0.1, 0.08, 0.1], [x, 0.24, 0]));
    g.add(part(G.cyl, metal, [0.02, 0.36, 0.02], [x + 0.18, 0.26, 0.04], [0, 0, Math.PI / 2]));
    g.add(part(G.cyl, metal, [0.02, 0.36, 0.02], [x + 0.18, 0.26, -0.04], [0, 0, Math.PI / 2]));
  }
  return g;
}

const BUILDERS = { infantry, tank, artillery, harvester, helicopter, jet, gunboat, destroyer, battleship };

/** A unit body (no base disc or HP bar). `paint` is the material for faction-colored parts. */
export function buildUnit(type, owner, paint) {
  const c = factionColors(owner);
  const g = (BUILDERS[type] ?? infantry)(paint ?? mat(c.paint), mat(c.dark));
  g.traverse((o) => { if (o.isMesh) o.userData.unitPart = true; });
  return g;
}

// ---------------------------------------------------------------- command base
export function buildBase(owner) {
  const c = factionColors(owner);
  const paint = mat(c.paint), dark = mat(c.dark), stone = mat(PALETTE.stone);
  const g = new THREE.Group();
  g.add(
    part(G.cyl6, stone, [0.78, 0.08, 0.78], [0, 0.04, 0], [0, Math.PI / 6, 0]),        // pad
    part(G.box, dark, [0.62, 0.3, 0.62], [0, 0.23, 0]),                                // walls
    paintPart(G.box, paint, [0.42, 0.34, 0.42], [0, 0.42, 0]),                         // keep
    part(G.box, dark, [0.48, 0.05, 0.48], [0, 0.61, 0]),                               // roof lip
    part(G.dome, mat(0xd8dde4), [0.13, 0.1, 0.13], [0.08, 0.64, 0.08], [Math.PI / 5, 0, 0]), // radar dish
    part(G.cyl, mat(PALETTE.metal), [0.012, 0.42, 0.012], [-0.12, 0.84, -0.12]),       // mast
    part(G.plane, mat(c.paint, { side: THREE.DoubleSide }), [0.2, 0.12, 1], [-0.02, 0.98, -0.12]), // flag
  );
  for (const [x, z] of [[0.31, 0.31], [-0.31, 0.31], [0.31, -0.31], [-0.31, -0.31]]) {
    g.add(part(G.cyl6, dark, [0.09, 0.42, 0.09], [x, 0.29, z]), part(G.cone, mat(c.trim), [0.1, 0.12, 0.1], [x, 0.56, z]));
  }
  return g;
}

// ---------------------------------------------------------------- defensive structures
/** Gun turret, AA battery, dock or coastal gun; the part named "head" turns toward its target. */
export function buildStructure(type, owner) {
  const c = factionColors(owner), paint = mat(c.paint), dark = mat(c.dark), metal = mat(PALETTE.metal);
  if (type === "dock") {
    const g = new THREE.Group();
    g.add(
      part(G.box, mat(0x6b5340), [0.85, 0.06, 0.34], [0.1, 0.05, 0]),
      part(G.box, mat(0x6b5340), [0.2, 0.05, 0.7], [-0.28, 0.04, 0]),
      paintPart(G.box, paint, [0.28, 0.22, 0.24], [-0.22, 0.18, 0.18]),
      part(G.box, dark, [0.3, 0.04, 0.26], [-0.22, 0.3, 0.18]),
      part(G.cyl, metal, [0.02, 0.28, 0.02], [0.22, 0.2, 0.1]),
    );
    return g;
  }
  if (type === "coastal") {
    const g = new THREE.Group();
    g.add(
      part(G.box, mat(PALETTE.stone), [0.55, 0.16, 0.5], [0, 0.1, 0]),
      paintPart(G.box, paint, [0.36, 0.1, 0.36], [0, 0.22, 0]),
    );
    const head = new THREE.Group();
    head.name = "head";
    head.position.y = 0.28;
    head.add(paintPart(G.cyl6, paint, [0.16, 0.1, 0.16], [0, 0.06, 0]));
    head.add(part(G.cyl, metal, [0.03, 0.55, 0.03], [0.28, 0.1, 0], [0, 0, Math.PI / 2]));
    g.add(head);
    return g;
  }
  const g = new THREE.Group();
  g.add(
    part(G.cyl6, mat(PALETTE.stone), [0.5, 0.08, 0.5], [0, 0.04, 0], [0, Math.PI / 6, 0]),   // pad
    part(G.cyl, dark, [0.34, 0.2, 0.34], [0, 0.18, 0]),                                        // emplacement
    paintPart(G.cyl, paint, [0.36, 0.04, 0.36], [0, 0.3, 0]),                                  // faction band
  );
  for (let k = 0; k < 6; k++) {                                                                 // sandbags
    const a = (k * Math.PI) / 3;
    g.add(part(G.box, mat(0x9b8a62), [0.14, 0.07, 0.07], [Math.cos(a) * 0.42, 0.11, Math.sin(a) * 0.42], [0, -a, 0]));
  }
  const head = new THREE.Group();
  head.name = "head";
  head.position.y = 0.34;
  if (type === "aa") {
    head.add(paintPart(G.box, paint, [0.22, 0.1, 0.24], [0, 0.05, 0]));
    for (const z of [-0.07, 0.07]) for (const y of [0.1, 0.16]) {
      head.add(part(G.cyl, metal, [0.016, 0.42, 0.016], [0.12, y + 0.12, z], [0, 0, -0.9]));
    }
    head.add(part(G.dome, mat(0xd8dde4), [0.08, 0.06, 0.08], [-0.1, 0.16, 0], [0, 0, Math.PI / 2])); // radar
  } else {
    head.add(paintPart(G.cyl6, paint, [0.18, 0.12, 0.18], [0, 0.06, 0]));
    head.add(part(G.box, dark, [0.14, 0.06, 0.2], [-0.06, 0.13, 0]));
    for (const z of [-0.05, 0.05]) head.add(part(G.cyl, metal, [0.022, 0.42, 0.022], [0.26, 0.08, z], [0, 0, Math.PI / 2]));
  }
  g.add(head);
  return g;
}

// ---------------------------------------------------------------- traversal props (world space)
// `from` / `to` are world-space {x, z} hex centres; heights in world units.

const APOTHEM = 0.985 * Math.cos(Math.PI / 6); // distance from hex centre to a flat side (tile radius 0.985)

/** Ladder fixed to the plateau's cliff face, facing the ground hex. */
export function buildLadder(ground, plateau, groundY, topY) {
  const g = new THREE.Group();
  const dx = ground.x - plateau.x, dz = ground.z - plateau.z, len = Math.hypot(dx, dz);
  const ux = dx / len, uz = dz / len;
  g.position.set(plateau.x + ux * (APOTHEM + 0.04), groundY, plateau.z + uz * (APOTHEM + 0.04));
  g.rotation.y = Math.atan2(-uz, ux); // local +x points away from the cliff
  const h = topY - groundY + 0.18, orange = mat(PALETTE.orange);
  for (const z of [-0.15, 0.15]) g.add(part(G.box, orange, [0.04, h, 0.04], [0, h / 2, z]));
  for (let y = 0.12; y < h - 0.05; y += 0.15) g.add(part(G.box, orange, [0.03, 0.03, 0.3], [0, y, 0]));
  return g;
}

/** Stair run on the ground hex climbing to the plateau edge. */
export function buildStairs(ground, plateau, groundY, topY, steps = 6) {
  const g = new THREE.Group();
  const dx = plateau.x - ground.x, dz = plateau.z - ground.z, len = Math.hypot(dx, dz);
  const ux = dx / len, uz = dz / len;
  const face = len - APOTHEM, run = 0.8, depth = run / steps;
  g.position.set(ground.x, groundY, ground.z);
  g.rotation.y = Math.atan2(-uz, ux); // local +x runs uphill
  const stone = mat(PALETTE.stone), nose = mat(PALETTE.orange);
  for (let i = 0; i < steps; i++) {
    const top = ((topY - groundY) * (i + 1)) / steps;
    const x = face - run + depth * (i + 0.5);
    g.add(part(G.box, stone, [depth, top, 0.5], [x, top / 2, 0]));
    g.add(part(G.box, nose, [0.03, 0.02, 0.5], [x + depth / 2 - 0.015, top + 0.005, 0]));
  }
  return g;
}

/** Jump pad on a tower top, chevron pointing at its partner pad. */
export function buildJumpPad(at, toward, y) {
  const g = new THREE.Group();
  g.position.set(at.x, y, at.z);
  g.rotation.y = Math.atan2(-(toward.z - at.z), toward.x - at.x);
  const orange = mat(PALETTE.orange, { emissive: 0x4a2200 });
  g.add(
    part(G.cyl6, mat(PALETTE.metal), [0.36, 0.04, 0.36], [0, 0.02, 0], [0, Math.PI / 6, 0]),
    part(G.cyl6, orange, [0.3, 0.05, 0.3], [0, 0.05, 0], [0, Math.PI / 6, 0]),
    part(G.cone4, mat(0xfff1d6), [0.1, 0.22, 0.04], [0.08, 0.09, 0], [0, 0, -Math.PI / 2]),
  );
  return g;
}

/** Dashed arc between two jump pads. */
export function buildJumpArc(a, b, y) {
  const pts = [];
  for (let i = 0; i <= 24; i++) {
    const t = i / 24;
    pts.push(new THREE.Vector3(a.x + (b.x - a.x) * t, y + 0.12 + Math.sin(Math.PI * t) * 0.9, a.z + (b.z - a.z) * t));
  }
  const line = new THREE.Line(
    new THREE.BufferGeometry().setFromPoints(pts),
    new THREE.LineDashedMaterial({ color: PALETTE.orange, dashSize: 0.12, gapSize: 0.08, transparent: true, opacity: 0.9 }),
  );
  line.computeLineDistances();
  return line;
}

/** Instanced spikes for every pit hex. */
export function buildSpikes(centres, y) {
  const offsets = [[0, 0]];
  for (let k = 0; k < 6; k++) offsets.push([Math.cos((k * Math.PI) / 3) * 0.34, Math.sin((k * Math.PI) / 3) * 0.34]);
  for (let k = 0; k < 6; k++) offsets.push([Math.cos(((k + 0.5) * Math.PI) / 3) * 0.62, Math.sin(((k + 0.5) * Math.PI) / 3) * 0.62]);
  const mesh = new THREE.InstancedMesh(G.cone, mat(PALETTE.spike), Math.max(1, centres.length * offsets.length));
  const d = new THREE.Object3D();
  let i = 0;
  for (const c of centres) {
    for (const [ox, oz] of offsets) {
      const h = 0.26 + ((i * 37) % 7) * 0.02;
      d.position.set(c.x + ox, y + h / 2, c.z + oz);
      d.scale.set(0.08, h, 0.08);
      d.updateMatrix();
      mesh.setMatrixAt(i++, d.matrix);
    }
  }
  mesh.count = i;
  return mesh;
}

/** Rocky crown so mountains read as impassable peaks, not tall plateaus. */
export function buildPeak(c, y, seed = 0) {
  const g = new THREE.Group();
  const rock = mat(PALETTE.rock);
  g.add(part(G.cone, rock, [0.62, 0.7, 0.62], [c.x, y + 0.33, c.z], [0, seed, 0]));
  g.add(part(G.cone, mat(0xd9dee6), [0.2, 0.2, 0.2], [c.x, y + 0.6, c.z], [0, seed, 0])); // snow cap
  for (let k = 0; k < 2; k++) {
    const a = seed * 2 + k * 2.4;
    g.add(part(G.rock, rock, [0.14, 0.11, 0.14], [c.x + Math.cos(a) * 0.5, y + 0.06, c.z + Math.sin(a) * 0.5], [seed, a, 0]));
  }
  return g;
}

/** A small cluster of ore crystals. */
export function buildOre(c, y, seed = 0) {
  const g = new THREE.Group();
  const ore = mat(PALETTE.ore, { emissive: 0x5a3c00 });
  for (let k = 0; k < 3; k++) {
    const a = seed + k * 2.1, r = 0.38;
    const s = 0.07 + k * 0.015;
    g.add(part(G.octa, ore, [s, s * 1.8, s], [c.x + Math.cos(a) * r, y + s * 1.4, c.z + Math.sin(a) * r], [0, a, 0.25]));
  }
  return g;
}

/** Instanced trees: three per forest hex, two foliage tiers on a trunk. `spots`: [{ x, y, z, seed }]. */
export function buildForest(spots) {
  const OFF = [[0.32, 0.12], [-0.28, 0.24], [0.02, -0.34]];
  const n = spots.length * OFF.length;
  const trunk = new THREE.InstancedMesh(G.cyl, mat(0x5a3d26), n);
  const low = new THREE.InstancedMesh(G.cone, mat(0x24572e), n);
  const high = new THREE.InstancedMesh(G.cone, mat(0x2f6e3a), n);
  const d = new THREE.Object3D();
  let i = 0;
  for (const s of spots) {
    for (const [ox, oz] of OFF) {
      const k = 0.8 + ((s.seed * 9973 + i * 0.37) % 1) * 0.45; // per-tree size
      const x = s.x + ox, z = s.z + oz;
      const put = (mesh, sx, sy, y) => { d.position.set(x, s.y + y, z); d.scale.set(sx, sy, sx); d.rotation.set(0, s.seed * 6 + i, 0); d.updateMatrix(); mesh.setMatrixAt(i, d.matrix); };
      put(trunk, 0.05 * k, 0.22 * k, 0.11 * k);
      put(low, 0.24 * k, 0.38 * k, 0.38 * k);
      put(high, 0.17 * k, 0.32 * k, 0.6 * k);
      i++;
    }
  }
  const g = new THREE.Group();
  g.add(trunk, low, high);
  return g;
}

/** Instanced houses for towns (ruins hexes): two per hex, box walls with a pyramid roof. */
export function buildTown(spots) {
  const OFF = [[0.22, -0.12, 0.3], [-0.24, 0.18, 1.9]];
  const n = spots.length * OFF.length;
  const walls = new THREE.InstancedMesh(G.box, mat(0xcdbfa6), n);
  const roofs = new THREE.InstancedMesh(G.cone4, mat(0x8a3b2c), n);
  const d = new THREE.Object3D();
  let i = 0;
  for (const s of spots) {
    for (const [ox, oz, rot] of OFF) {
      const yaw = rot + s.seed * 3, w = 0.3 + (s.seed * 7 % 1) * 0.08;
      d.rotation.set(0, yaw, 0);
      d.position.set(s.x + ox, s.y + 0.11, s.z + oz); d.scale.set(w, 0.22, 0.24); d.updateMatrix(); walls.setMatrixAt(i, d.matrix);
      d.rotation.set(0, yaw + Math.PI / 4, 0);
      d.position.set(s.x + ox, s.y + 0.3, s.z + oz); d.scale.set(w * 0.78, 0.18, w * 0.78); d.updateMatrix(); roofs.setMatrixAt(i, d.matrix);
      i++;
    }
  }
  const g = new THREE.Group();
  g.add(walls, roofs);
  return g;
}
